<template>
  <article class="article-card">
    <router-link 
      :to="{ name: 'Article', params: { section: section, id: article.id } }" 
      class="article-link"
    >
      <div class="article-image">
        <img :src="article.image" :alt="article.title" />
        <div class="article-category">
          {{ section }}
        </div>
      </div>
      <div class="article-content">
        <div class="article-meta">
          <span class="article-date">{{ formattedDate }}</span>
          <span class="article-read-time">{{ readTime }} min read</span>
        </div>
        <h3 class="article-title">{{ article.title }}</h3>
        <p class="article-summary">{{ article.summary }}</p>
        <div class="article-footer">
          <span class="read-more">
            Read Full Article
            <svg class="arrow-icon" viewBox="0 0 24 24" fill="none">
              <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </div>
      </div>
    </router-link>
  </article>
</template>

<script>
export default {
  name: 'ArticleCard',
  props: {
    article: {
      type: Object,
      required: true,
    },
    section: {
      type: String,
      required: true,
    },
  },
  computed: {
    formattedDate() {
      if (!this.article.date) return '';
      const date = new Date(this.article.date);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    readTime() {
      // Estimate read time based on summary length
      // Average reading speed is ~200 words per minute
      const wordCount = this.article.summary ? this.article.summary.split(' ').length : 0;
      return Math.max(1, Math.ceil(wordCount / 50)); // Estimate for card summary + article
    },
  },
};
</script>

<style scoped>
.article-card {
  background: var(--neutral-100);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  overflow: hidden;
  transition: all var(--transition-normal);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.article-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-xl);
}

.article-link {
  display: flex;
  flex-direction: column;
  height: 100%;
  text-decoration: none;
  color: inherit;
}

.article-image {
  position: relative;
  height: 240px;
  overflow: hidden;
}

.article-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform var(--transition-slow);
}

.article-card:hover .article-image img {
  transform: scale(1.05);
}

.article-category {
  position: absolute;
  top: var(--space-4);
  left: var(--space-4);
  background: var(--primary-color);
  color: var(--neutral-100);
  padding: var(--space-1) var(--space-3);
  border-radius: var(--radius-full);
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: capitalize;
  letter-spacing: 0.05em;
}

.article-content {
  padding: var(--space-6);
  flex: 1;
  display: flex;
  flex-direction: column;
}

.article-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-4);
  font-size: var(--text-sm);
  color: var(--neutral-500);
}

.article-date {
  font-weight: 500;
}

.article-read-time {
  color: var(--neutral-400);
}

.article-title {
  font-family: var(--font-display);
  font-size: var(--text-xl);
  font-weight: 600;
  color: var(--neutral-900);
  margin-bottom: var(--space-3);
  line-height: var(--leading-tight);
  transition: color var(--transition-fast);
}

.article-card:hover .article-title {
  color: var(--primary-color);
}

.article-summary {
  color: var(--neutral-600);
  line-height: var(--leading-relaxed);
  margin-bottom: var(--space-6);
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.article-footer {
  margin-top: auto;
}

.read-more {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--primary-color);
  font-weight: 500;
  font-size: var(--text-sm);
  transition: gap var(--transition-fast);
}

.article-card:hover .read-more {
  gap: var(--space-3);
}

.arrow-icon {
  width: 16px;
  height: 16px;
  transition: transform var(--transition-fast);
}

.article-card:hover .arrow-icon {
  transform: translate(2px, -2px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .article-image {
    height: 200px;
  }
  
  .article-content {
    padding: var(--space-4);
  }
  
  .article-title {
    font-size: var(--text-lg);
  }
  
  .article-summary {
    -webkit-line-clamp: 2;
  }
  
  .article-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-1);
  }
}

@media (max-width: 480px) {
  .article-image {
    height: 180px;
  }
  
  .article-content {
    padding: var(--space-3);
  }
  
  .article-title {
    font-size: var(--text-base);
    margin-bottom: var(--space-2);
  }
  
  .article-summary {
    font-size: var(--text-sm);
    margin-bottom: var(--space-4);
  }
}
</style>